import { Rect } from '../primitives/rect';
import { Size } from '../primitives/size';
import { processPathData, splitArrayCollection, transformPath } from './path-util';
import { whiteSpaceToString, wordBreakToString, textAlignToString, bBoxText, cloneObject } from './base-util';
import { identityMatrix, transformPointByMatrix, rotateMatrix } from '../primitives/matrix';
import { compile, createElement, Browser } from '@syncfusion/ej2-base';
import { Node } from '../objects/node';
import { getElement } from './diagram-util';
import { templateCompiler } from '../utility/base-util';
import { Diagram } from './../diagram';
import { FlipDirection } from '../enum/enum';
/**
 * Defines the functionalities that need to access DOM
 */
/**
 * removeElementsByClass method \
 *
 * @returns {void} removeElementsByClass method .\
 * @param { string } className - provide the element  value.
 * @param {string} id - provide the string  value.
 * @private
 */
export function removeElementsByClass(className, id) {
    let elements;
    if (id && document.getElementById(id).classList.contains(className)) {
        elements = document.getElementById(id).getElementsByClassName(className);
    }
    else {
        elements = document.getElementsByClassName(className);
    }
    while (elements.length > 0) {
        elements[0].parentNode.removeChild(elements[0]);
    }
}
/**
 * findSegmentPoints method \
 *
 * @returns {PointModel[]} findSegmentPoints method .\
 * @param { PathElement } element - provide the element  value.
 * @private
 */
export function findSegmentPoints(element) {
    let pts = [];
    let sample;
    let sampleLength;
    const measureWindowElement = 'measureElement';
    window[`${measureWindowElement}`].style.visibility = 'visible';
    const svg = window[`${measureWindowElement}`].children[2];
    const pathNode = getChildNode(svg)[0];
    pathNode.setAttributeNS(null, 'd', element.data);
    const pathBounds = element.absoluteBounds; // || pathNode.getBBox();
    const pathData = updatePath(element, pathBounds, element);
    pathNode.setAttributeNS(null, 'd', pathData);
    const pathLength = pathNode.getTotalLength();
    // 930450: Diagram Taking Too Long to Load Due to Complex Hierarchical Tree Layout with Path Nodes
    const storedPoints = Diagram.prototype.getPathData(pathData);
    if (storedPoints.length === 0) {
        for (sampleLength = 0; sampleLength <= pathLength; sampleLength += 10) {
            sample = pathNode.getPointAtLength(sampleLength);
            pts.push({ x: sample.x, y: sample.y });
        }
        // Push the calculated points into the shared storage
        Diagram.prototype.setPathData(pathData, pts);
    }
    else {
        pts = storedPoints;
    }
    window[`${measureWindowElement}`].style.visibility = 'hidden';
    return pts;
}
/**
 * getChildNode method \
 *
 * @returns {SVGElement[] | HTMLCollection} findSegmentPoints method .\
 * @param { SVGElement } node - provide the element  value.
 * @private
 */
export function getChildNode(node) {
    let child;
    let collection = [];
    if (Browser.info.name === 'msie' || Browser.info.name === 'edge') {
        for (let i = 0; i < node.childNodes.length; i++) {
            child = node.childNodes[parseInt(i.toString(), 10)];
            if (child.nodeType === 1) {
                collection.push(child);
            }
        }
    }
    else {
        collection = node.children;
    }
    return collection;
}
/**
 * translatePoints method \
 *
 * @returns {PointModel[]} translatePoints method .\
 * @param { SVGElement } element - provide the element  value.
 * @param { PointModel[] } points - provide the element  value.
 * @private
 */
export function translatePoints(element, points) {
    const translatedPts = [];
    //895069: Update Connector docking position in node after flipping the node
    const left = element.offsetX - element.actualSize.width * element.pivot.x;
    const top = element.offsetY - element.actualSize.height * element.pivot.y;
    for (const point of points) {
        let pt1;
        const baseX = left + point.x;
        const baseY = top + point.y;
        const flipX = left + element.actualSize.width - point.x;
        const flipY = top + element.actualSize.height - point.y;
        // 895069: Updating the node and connector's docking point for node's fliped position
        switch (element.flip) {
            case FlipDirection.Both:
                pt1 = { x: flipX, y: flipY };
                break;
            case FlipDirection.Horizontal:
                pt1 = { x: flipX, y: baseY };
                break;
            case FlipDirection.Vertical:
                pt1 = { x: baseX, y: flipY };
                break;
            default:
                pt1 = { x: baseX, y: baseY };
                break;
        }
        let matrix;
        const angle = element.rotateAngle + element.parentTransform;
        if (angle) {
            matrix = identityMatrix();
            rotateMatrix(matrix, angle, element.offsetX, element.offsetY);
        }
        if (matrix) {
            pt1 = transformPointByMatrix(matrix, pt1);
        }
        translatedPts.push(pt1);
    }
    return translatedPts;
}
// Cache to store connector decorator bounds
let decoratorPathCache = {};
/**
 * Function to clear the cache
 *
 * @returns {void}
 * @private
 */
export function clearDecoratorPathCache() {
    decoratorPathCache = {};
}
/**
 * measurePath method \
 *
 * @returns {Rect} measurePath method .\
 * @param { string } data - provide the element  value.
 * @private
 */
export function measurePath(data) {
    if (data) {
        // return the cached decorator path bounds.
        if (decoratorPathCache[`${data}`]) {
            return decoratorPathCache[`${data}`];
        }
        const measureWindowElement = 'measureElement';
        window[`${measureWindowElement}`].style.visibility = 'visible';
        const svg = window[`${measureWindowElement}`].children[2];
        const element = getChildNode(svg)[0];
        element.setAttribute('d', data);
        const bounds = element.getBBox();
        const svgBounds = new Rect(bounds.x, bounds.y, bounds.width, bounds.height);
        window[`${measureWindowElement}`].style.visibility = 'hidden';
        // Store the measured bounds in the cache.
        decoratorPathCache[`${data}`] = svgBounds;
        return svgBounds;
    }
    return new Rect(0, 0, 0, 0);
}
/**
 * getTextOptions method \
 *
 * @returns {BaseAttributes} getTextOptions method .\
 * @param { TextElement } element - provide the element  value.
 * @param { number } maxWidth - provide the maxWidth  value.
 * @private
 */
function getTextOptions(element, maxWidth) {
    const options = {
        fill: element.style.fill, stroke: element.style.strokeColor, angle: element.rotateAngle + element.parentTransform,
        pivotX: element.pivot.x, pivotY: element.pivot.y, strokeWidth: element.style.strokeWidth,
        dashArray: element.style.strokeDashArray, opacity: element.style.opacity, shadow: element.shadow,
        gradient: element.style.gradient, visible: element.visible, id: element.id, description: element.description,
        width: maxWidth || element.actualSize.width, height: element.actualSize.height,
        x: element.offsetX - element.actualSize.width * element.pivot.x + 0.5,
        y: element.offsetY - element.actualSize.height * element.pivot.y + 0.5
    };
    options.fontSize = element.style.fontSize;
    options.fontFamily = element.style.fontFamily;
    options.textOverflow = element.style.textOverflow;
    options.textDecoration = element.style.textDecoration;
    options.doWrap = element.doWrap;
    options.whiteSpace = whiteSpaceToString(element.style.whiteSpace, element.style.textWrapping);
    options.content = element.content;
    options.textWrapping = element.style.textWrapping;
    options.breakWord = wordBreakToString(element.style.textWrapping);
    options.textAlign = textAlignToString(element.style.textAlign);
    options.color = element.style.color;
    options.italic = element.style.italic;
    options.bold = element.style.bold;
    options.dashArray = '';
    options.strokeWidth = 0;
    options.fill = '';
    return options;
}
/**
 * wrapSvgText method \
 *
 * @returns {SubTextElement[]} wrapSvgText method .\
 * @param { TextAttributes } text - provide the element  value.
 * @param { string } textValue - provide the maxWidth  value.
 * @param { number } laneWidth - provide the maxWidth  value.
 * @private
 */
function wrapSvgText(text, textValue, laneWidth) {
    let childNodes = [];
    let k = 0;
    let txtValue;
    let bounds1;
    const content = textValue || text.content;
    if (text.whiteSpace !== 'nowrap' && text.whiteSpace !== 'pre') {
        if (text.breakWord === 'breakall') {
            txtValue = '';
            txtValue += content[0];
            for (k = 0; k < content.length; k++) {
                bounds1 = bBoxText(txtValue, text);
                if (bounds1 >= text.width && txtValue.length > 0) {
                    childNodes[childNodes.length] = { text: txtValue, x: 0, dy: 0, width: bounds1 };
                    txtValue = '';
                }
                else {
                    txtValue = txtValue + (content[k + 1] || '');
                    if (txtValue.indexOf('\n') > -1) {
                        childNodes[childNodes.length] = { text: txtValue, x: 0, dy: 0, width: bBoxText(txtValue, text) };
                        txtValue = '';
                    }
                    const width = bBoxText(txtValue, text);
                    if (Math.ceil(width) + 2 >= text.width && txtValue.length > 0) {
                        childNodes[childNodes.length] = { text: txtValue, x: 0, dy: 0, width: width };
                        txtValue = '';
                    }
                    if (k === content.length - 1 && txtValue.length > 0) {
                        childNodes[childNodes.length] = { text: txtValue, x: 0, dy: 0, width: width };
                        txtValue = '';
                    }
                }
            }
        }
        else {
            childNodes = wordWrapping(text, textValue, laneWidth);
        }
    }
    else {
        childNodes[childNodes.length] = { text: content, x: 0, dy: 0, width: bBoxText(content, text) };
    }
    return childNodes;
}
/**
 * wordWrapping method \
 *
 * @returns {SubTextElement[]} wordWrapping method .\
 * @param { TextAttributes } text - provide the element  value.
 * @param { string } textValue - provide the maxWidth  value.
 * @param { number } laneWidth - provide the maxWidth  value.
 * @private
 */
function wordWrapping(text, textValue, laneWidth) {
    const childNodes = [];
    let txtValue = '';
    let j = 0;
    let i = 0;
    const wrap = text.whiteSpace !== 'nowrap' ? true : false;
    const content = textValue || text.content;
    const eachLine = content.split('\n');
    let txt;
    let words;
    let newText;
    let existingWidth;
    let existingText;
    for (j = 0; j < eachLine.length; j++) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        txt = '';
        words = text.textWrapping !== 'NoWrap' ? eachLine[parseInt(j.toString(), 10)].split(' ') : [eachLine[parseInt(j.toString(), 10)]];
        for (i = 0; i < words.length; i++) {
            txtValue += (((i !== 0 || words.length === 1) && wrap && txtValue.length > 0) ? ' ' : '') + words[parseInt(i.toString(), 10)];
            //Bug 885842: Position of annotation inside the node is not aligned center.
            //Extra space is added when we have single word as annotation text and due to this the width of the text is increased.
            if (words[i + 1]) {
                newText = txtValue + ' ' + (words[i + 1]);
            }
            else {
                newText = txtValue;
            }
            const width = bBoxText(newText, text);
            if (Math.floor(width) > (laneWidth || text.width) - 2 && txtValue.length > 0) {
                childNodes[childNodes.length] = {
                    text: txtValue, x: 0, dy: 0,
                    width: newText === txtValue ? width : (txtValue === existingText) ? existingWidth :
                        bBoxText(txtValue, text)
                };
                txtValue = '';
            }
            else {
                if (i === words.length - 1) {
                    childNodes[childNodes.length] = { text: txtValue, x: 0, dy: 0, width: width };
                    txtValue = '';
                }
            }
            existingText = newText;
            existingWidth = width;
        }
    }
    return childNodes;
}
/**
 * wrapSvgTextAlign method \
 *
 * @returns {TextBounds} wrapSvgTextAlign method .\
 * @param { TextAttributes } text - provide the element  value.
 * @param { string } childNodes - provide the maxWidth  value.
 * @private
 */
function wrapSvgTextAlign(text, childNodes) {
    const wrapBounds = { x: 0, width: 0 };
    let k = 0;
    let txtWidth;
    let width;
    for (k = 0; k < childNodes.length; k++) {
        txtWidth = childNodes[parseInt(k.toString(), 10)].width;
        width = txtWidth;
        //EJ2-863489 - Node annotation textAlign "Justify" option is not working correctly
        if (text.textAlign === 'left' || text.textAlign === 'justify') {
            txtWidth = 0;
        }
        else if (text.textAlign === 'center') {
            if (txtWidth > text.width && (text.textOverflow === 'Ellipsis' || text.textOverflow === 'Clip')) {
                txtWidth = 0;
            }
            else {
                txtWidth = -txtWidth / 2;
            }
        }
        else if (text.textAlign === 'right') {
            txtWidth = -txtWidth;
        }
        else {
            txtWidth = childNodes.length > 1 ? 0 : -txtWidth / 2;
        }
        childNodes[parseInt(k.toString(), 10)].dy = text.fontSize * 1.2;
        childNodes[parseInt(k.toString(), 10)].x = txtWidth;
        wrapBounds.x = Math.min(wrapBounds.x, txtWidth);
        wrapBounds.width = Math.max(wrapBounds.width, width);
    }
    return wrapBounds;
}
/**
 * measureHtmlText method \
 *
 * @returns {TextBounds} measureHtmlText method .\
 * @param { TextStyleModel } style - provide the style  value.
 * @param { string } content - provide the content  value.
 * @param { string } width - provide the width  value.
 * @param { string } height - provide the height  value.
 * @param { string } maxWidth - provide the maxWidth  value.
 * @private
 */
export function measureHtmlText(style, content, width, height, maxWidth) {
    const bounds = new Size();
    // 986032 - Annotation text field size updates incorrectly when the display style is set to flex on the body element
    const text = createHtmlElement('span', {
        'style': 'position:absolute; display:inline-block; line-height:normal; left:0px; top:0px'
    });
    if (style.bold) {
        text.style.fontWeight = 'bold';
    }
    if (style.italic) {
        text.style.fontStyle = 'italic';
    }
    if (width !== undefined) {
        text.style.width = width.toString() + 'px';
    }
    if (height !== undefined) {
        text.style.height = height.toString() + 'px';
    }
    if (maxWidth !== undefined) {
        text.style.maxWidth = maxWidth.toString() + 'px';
    }
    text.style.fontFamily = style.fontFamily;
    text.style.fontSize = style.fontSize + 'px';
    text.style.color = style.color;
    text.textContent = content;
    text.style.whiteSpace = whiteSpaceToString(style.whiteSpace, style.textWrapping);
    if (maxWidth !== undefined) {
        text.style.wordBreak = 'break-word';
    }
    else {
        text.style.wordBreak = wordBreakToString(style.textWrapping);
    }
    document.body.appendChild(text);
    bounds.width = text.offsetWidth;
    bounds.height = text.offsetHeight;
    document.body.removeChild(text);
    return bounds;
}
/**
 * measureText method \
 *
 * @returns {Size} measureText method .\
 * @param { TextStyleModel } text - provide the text  value.
 * @param { string } style - provide the style  value.
 * @param { string } content - provide the content  value.
 * @param { number } maxWidth - provide the maxWidth  value.
 * @param { string } textValue - provide the textValue  value.
 * @private
 */
export function measureText(text, style, content, maxWidth, textValue) {
    const bounds = new Size(0, 0);
    let childNodes;
    let wrapBounds;
    const options = getTextOptions(text, maxWidth);
    text.childNodes = childNodes = wrapSvgText(options, textValue, text.isLaneOrientation ? maxWidth : undefined);
    text.wrapBounds = wrapBounds = wrapSvgTextAlign(options, childNodes);
    bounds.width = wrapBounds.width;
    if (text.wrapBounds.width >= maxWidth && options.textOverflow !== 'Wrap') {
        bounds.width = maxWidth;
    }
    bounds.height = childNodes.length * text.style.fontSize * 1.2;
    return bounds;
}
/**
 * measureImage method \
 *
 * @returns {Size} measureImage method .\
 * @param { string } source - provide the text  value.
 * @param { Size } contentSize - provide the style  value.
 * @param { string } id - provide the content  value.
 * @param { Function } callback - provide the maxWidth  value.
 * @private
 */
// eslint-disable-next-line
export function measureImage(source, contentSize, id, callback) {
    const measureWindowElement = 'measureElement';
    window[`${measureWindowElement}`].style.visibility = 'visible';
    const imageElement = window[`${measureWindowElement}`].children[1];
    imageElement.setAttribute('src', source);
    const bounds = imageElement.getBoundingClientRect();
    const width = bounds.width;
    const height = bounds.height;
    contentSize = new Size(width, height);
    window[`${measureWindowElement}`].style.visibility = 'hidden';
    const element = document.createElement('img');
    element.setAttribute('src', source);
    setAttributeHtml(element, { id: id + 'sf-imageNode' });
    element.style.display = 'none';
    document.body.appendChild(element);
    // eslint-disable-next-line
    element.onload = (event) => {
        const loadedImage = event.currentTarget;
        if (callback) {
            callback(id, { width: loadedImage.width, height: loadedImage.height });
        }
    };
    return contentSize;
}
/* eslint-disable */
/**
 * measureNativeContent method \
 *
 * @returns {Rect} measureNativeContent method .\
 * @param { SVGElement } nativeContent - provide the text  value.
 * @private
 */
export function measureNativeContent(nativeContent) {
    const measureWindowElement = 'measureElement';
    let nativeSVG = window[measureWindowElement].children[2];
    nativeSVG.appendChild(nativeContent);
    const bounds = nativeContent.getBoundingClientRect();
    const svgBounds = nativeSVG.getBoundingClientRect();
    const rect = bounds;
    rect.x = bounds.left - svgBounds.left;
    rect.y = bounds.top - svgBounds.top;
    nativeSVG.removeChild(nativeContent);
    return rect;
}
/**
 * measureNativeSvg method \
 *
 * @returns {Rect} measureNativeSvg method .\
 * @param { SVGElement } nativeContent - provide the text  value.
 * @private
 */
export function measureNativeSvg(nativeContent) {
    const measureWindowElement = 'measureElement';
    window[measureWindowElement].style.visibility = 'visible';
    const nativeSVG = window[measureWindowElement].children[2];
    nativeSVG.appendChild(nativeContent);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const svgBounds = nativeSVG.getBoundingClientRect();
    nativeSVG.removeChild(nativeContent);
    window[measureWindowElement].style.visibility = 'hidden';
    return svgBounds;
}
/**
 * updatePath method \
 *
 * @returns {string} updatePath method .\
 * @param { SVGElement } element - provide the element  value.
 * @param { Rect } bounds - provide the bounds  value.
 * @param { PathElement } child - provide the child  value.
 * @param { BaseAttributes } options - provide the options  value.
 * @private
 */
export function updatePath(element, bounds, child, options) {
    const initX = 0;
    const initY = 0;
    let scaleX = 0;
    let scaleY = 0;
    let isScale = false;
    let newPathString = '';
    let arrayCollection = [];
    const bBox = bounds;
    if (initX !== bBox.x || initY !== bBox.y) {
        scaleX = initX - Number(bBox.x);
        scaleY = initY - Number(bBox.y);
    }
    if (element.actualSize.width !== bBox.width || element.actualSize.height !== bBox.height || options) {
        scaleX = (options && options.width || element.actualSize.width) / Number(bBox.width ? bBox.width : 1);
        scaleY = (options && options.height || element.actualSize.height) / Number(bBox.height ? bBox.height : 1);
        isScale = true;
    }
    arrayCollection = processPathData(element.data);
    arrayCollection = splitArrayCollection(arrayCollection);
    newPathString = transformPath(arrayCollection, scaleX, scaleY, isScale, bBox.x, bBox.y, initX, initY);
    isScale = false;
    return newPathString;
}
/**
 * getDiagramLayerSvg method \
 *
 * @returns {string} getDiagramLayerSvg method .\
 * @param { string } diagramId - provide the element  value.
 * @private
 */
export function getDiagramLayerSvg(diagramId) {
    //let diagramLayerSvg: SVGSVGElement;
    const diagramElement = getDiagramElement(diagramId);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const elementcoll = diagramElement.getElementsByClassName('e-diagram-layer');
    const diagramLayerSvg = elementcoll[0];
    return diagramLayerSvg;
}
/**
 * getDiagramElement method \
 *
 * @returns {HTMLElement} getDiagramElement method .\
 * @param { string } elementId - provide the elementId  value.
 * @param { string } contentId - provide the elementId  value.
 * @private
 */
export function getDiagramElement(elementId, contentId) {
    let diagramElement;
    let element;
    if (contentId) {
        element = document.getElementById(contentId);
    }
    if (Browser.info.name === 'msie' || Browser.info.name === 'edge') {
        diagramElement = (element) ? element.querySelector('#' + elementId) : document.getElementById(elementId);
    }
    else {
        diagramElement = (element) ? element.querySelector('#' + CSS.escape(elementId)) : document.getElementById(elementId);
    }
    return diagramElement;
}
/**
 * getDomIndex method \
 *
 * @returns {HTMLElement} getDomIndex method .\
 * @param { string } viewId - provide the elementId  value.
 * @param { string } elementId - provide the elementId  value.
 * @param { string } layer - provide the elementId  value.
 * @private
 */
export function getDomIndex(viewId, elementId, layer) {
    let index;
    let parentElement;
    let postId = '';
    if (layer === 'native') {
        parentElement = getNativeLayer(viewId);
        postId = '_content_groupElement';
    }
    else if (layer === 'html') {
        parentElement = getHTMLLayer(viewId).childNodes[0];
        postId = '_html_element';
    }
    else {
        parentElement = getDiagramLayer(viewId);
        postId = '_groupElement';
    }
    const targetId = elementId + postId;
    const targetElement = document.getElementById(targetId);
    if (targetElement && parentElement.contains(targetElement)) {
        return Array.prototype.indexOf.call(parentElement.childNodes, targetElement);
    }
    return index;
}
/**
 * getAdornerLayerSvg method \
 *
 * @returns {SVGSVGElement} getAdornerLayerSvg method .\
 * @param { string } diagramId - provide the diagramId  value.
 * @private
 */
export function getAdornerLayerSvg(diagramId) {
    let adornerLayerSvg = null;
    const diagramElement = getDiagramElement(diagramId);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const elementcoll = diagramElement.getElementsByClassName('e-adorner-layer');
    adornerLayerSvg = elementcoll[0];
    return adornerLayerSvg;
}
/**
 * getSelectorElement method \
 *
 * @returns {SVGSVGElement} getSelectorElement method .\
 * @param { string } diagramId - provide the diagramId  value.
 * @private
 */
export function getSelectorElement(diagramId) {
    let adornerLayer = null;
    const adornerSvg = getAdornerLayerSvg(diagramId);
    adornerLayer = adornerSvg.getElementById(diagramId + '_SelectorElement');
    return adornerLayer;
}
/**
 * getAdornerLayer method \
 *
 * @returns {SVGSVGElement} getAdornerLayer method .\
 * @param { string } diagramId - provide the diagramId  value.
 * @private
 */
export function getAdornerLayer(diagramId) {
    let adornerLayer = null;
    const diagramAdornerSvg = getAdornerLayerSvg(diagramId);
    adornerLayer = diagramAdornerSvg.getElementById(diagramId + '_diagramAdorner');
    return adornerLayer;
}
/**
 * getUserHandleLayer method \
 *
 * @returns {HTMLElement} getUserHandleLayer method .\
 * @param { string } diagramId - provide the diagramId  value.
 * @private
 */
export function getUserHandleLayer(diagramId) {
    let adornerLayer = null;
    const diagramUserHandleLayer = getDiagramElement(diagramId);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const elementcoll = diagramUserHandleLayer.getElementsByClassName('e-userHandle-layer');
    adornerLayer = elementcoll[0];
    return adornerLayer;
}
/**
 * getDiagramLayer method \
 *
 * @returns {HTMLElement} getDiagramLayer method .\
 * @param { string } diagramId - provide the diagramId  value.
 * @private
 */
export function getDiagramLayer(diagramId) {
    //let diagramLayer: SVGElement;
    const diagramLayerSvg = getDiagramLayerSvg(diagramId);
    const diagramLayer = diagramLayerSvg.getElementById(diagramId + '_diagramLayer');
    return diagramLayer;
}
/**
 * getPortLayerSvg method \
 *
 * @returns {SVGSVGElement} getPortLayerSvg method .\
 * @param { string } diagramId - provide the diagramId  value.
 * @private
 */
export function getPortLayerSvg(diagramId) {
    let adornerLayerSvg = null;
    const diagramElement = getDiagramElement(diagramId);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const elementcoll = diagramElement.getElementsByClassName('e-ports-expand-layer');
    adornerLayerSvg = elementcoll[0];
    return adornerLayerSvg;
}
/**
 * getNativeLayerSvg method \
 *
 * @returns {SVGSVGElement} getNativeLayerSvg method .\
 * @param { string } diagramId - provide the diagramId  value.
 * @private
 */
export function getNativeLayerSvg(diagramId) {
    let nativeLayerSvg;
    const diagramElement = getDiagramElement(diagramId);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const elementcoll = diagramElement.getElementsByClassName('e-native-layer');
    nativeLayerSvg = elementcoll[0];
    return nativeLayerSvg;
}
/**
 * getGridLayerSvg method \
 *
 * @returns {SVGSVGElement} getNativeLayerSvg method .\
 * @param { string } diagramId - provide the diagramId  value.
 * @private
 */
export function getGridLayerSvg(diagramId) {
    let gridLayerSvg = null;
    const diagramElement = getDiagramElement(diagramId);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const elementcoll = diagramElement.getElementsByClassName('e-grid-layer');
    gridLayerSvg = elementcoll[0];
    return gridLayerSvg;
}
/**
 * getBackgroundLayerSvg method \
 *
 * @returns {SVGSVGElement} getBackgroundLayerSvg method .\
 * @param { string } diagramId - provide the diagramId  value.
 * @private
 */
export function getBackgroundLayerSvg(diagramId) {
    let gridLayerSvg = null;
    const diagramElement = getDiagramElement(diagramId);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const elementcoll = diagramElement.getElementsByClassName('e-background-layer');
    return elementcoll[0].parentNode;
}
/**
 * getBackgroundImageLayer method \
 *
 * @returns {SVGSVGElement} getBackgroundImageLayer method .\
 * @param { string } diagramId - provide the diagramId  value.
 * @private
 */
export function getBackgroundImageLayer(diagramId) {
    let imageLayer = null;
    const diagramElement = getDiagramElement(diagramId);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const elementcoll = diagramElement.getElementsByClassName('e-background-image-layer');
    imageLayer = elementcoll[0];
    return imageLayer;
}
/**
 * getBackgroundLayer method \
 *
 * @returns {SVGSVGElement} getBackgroundLayer method .\
 * @param { string } diagramId - provide the diagramId  value.
 * @private
 */
export function getBackgroundLayer(diagramId) {
    let imageLayer = null;
    const diagramElement = getDiagramElement(diagramId);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const elementcoll = diagramElement.getElementsByClassName('e-background-layer');
    imageLayer = elementcoll[0];
    return imageLayer;
}
/**
 * getGridLayer method \
 *
 * @returns {SVGSVGElement} getGridLayer method .\
 * @param { string } diagramId - provide the diagramId  value.
 * @private
 */
export function getGridLayer(diagramId) {
    const domTable = 'domTable';
    let expandCollapse = null;
    if (!window[domTable][diagramId + '_gridline']) {
        const diagramGridSvg = getGridLayerSvg(diagramId);
        expandCollapse = diagramGridSvg.getElementById(diagramId + '_gridline');
        window[domTable][diagramId + '_gridline'] = expandCollapse;
    }
    else {
        expandCollapse = window[domTable][diagramId + '_gridline'];
    }
    return expandCollapse;
}
// /** @private */
// export function getExpandCollapseLayer(diagramId: string): SVGElement {
//     let expandCollapse: SVGElement = null;
//     let diagramPortSvg: SVGSVGElement = getPortLayerSvg(diagramId);
//     expandCollapse = diagramPortSvg.getElementById(diagramId + '_diagramExpander') as SVGElement;
//     return expandCollapse;
// }
// /** @private */
// export function getPortsLayer(diagramId: string): SVGElement {
//     let expandCollapse: SVGElement = null;
//     let diagramPortSvg: SVGSVGElement = getPortLayerSvg(diagramId);
//     expandCollapse = diagramPortSvg.getElementById(diagramId + '_diagramPorts') as SVGElement;
//     return expandCollapse;
// }
/**
 * getNativeLayer method \
 *
 * @returns {SVGSVGElement} getNativeLayer method .\
 * @param { string } diagramId - provide the diagramId  value.
 * @private
 */
export function getNativeLayer(diagramId) {
    let nativeLayer = null;
    const nativeLayerSvg = getNativeLayerSvg(diagramId);
    nativeLayer = nativeLayerSvg.getElementById(diagramId + '_nativeLayer');
    return nativeLayer;
}
/**
 * getHTMLLayer method \
 *
 * @returns {SVGSVGElement} getHTMLLayer method .\
 * @param { string } diagramId - provide the diagramId  value.
 * @private
 */
export function getHTMLLayer(diagramId) {
    let htmlLayer = null;
    const domTable = 'domTable';
    if (!window[domTable][diagramId + 'html_layer']) {
        const element = getDiagramElement(diagramId);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const elementcoll = element.getElementsByClassName('e-html-layer');
        htmlLayer = elementcoll[0];
        window[domTable][diagramId + 'html_layer'] = htmlLayer;
    }
    else {
        htmlLayer = window[domTable][diagramId + 'html_layer'];
    }
    return htmlLayer;
}
/* eslint-enable */
/**
 * createHtmlElement method \
 *
 * @returns {SVGSVGElement} createHtmlElement method .\
 * @param { string } elementType - provide the diagramId  value.
 * @param { Object } attribute - provide the diagramId  value.
 * @private
 */
export function createHtmlElement(elementType, attribute) {
    const element = createElement(elementType);
    setAttributeHtml(element, attribute);
    return element;
}
/**
 * createSvgElement method \
 *
 * @returns {SVGSVGElement} createSvgElement method .\
 * @param { string } elementType - provide the elementType  value.
 * @param { Object } attribute - provide the attribute  value.
 * @private
 */
export function createSvgElement(elementType, attribute) {
    const element = document.createElementNS('http://www.w3.org/2000/svg', elementType);
    setAttributeSvg(element, attribute);
    return element;
}
/** @hidden */
/**
 * parentsUntil method \
 *
 * @returns {SVGSVGElement} parentsUntil method .\
 * @param { Element } elem - provide the elementType  value.
 * @param { string } selector - provide the attribute  value.
 * @param { boolean } isID - provide the attribute  value.
 * @private
 */
export function parentsUntil(elem, selector, isID) {
    let parent = elem;
    while (parent) {
        if (isID ? parent.id === selector : hasClass(parent, selector)) {
            break;
        }
        parent = parent.parentNode;
    }
    return parent;
}
/**
 * hasClass method \
 *
 * @returns {SVGSVGElement} hasClass method .\
 * @param { HTMLElement } element - provide the element  value.
 * @param { string } className - provide the className  value.
 * @private
 */
export function hasClass(element, className) {
    const eClassName = (typeof element.className === 'object') ? element.className.animVal : element.className;
    return ((' ' + eClassName + ' ').indexOf(' ' + className + ' ') > -1) ? true : false;
}
/**
 * getScrollerWidth method \
 *
 * @returns {number} getScrollerWidth method .\
 * @private
 */
export function getScrollerWidth() {
    const outer = createHtmlElement('div', { 'style': 'visibility:hidden; width: 100px' });
    document.body.appendChild(outer);
    const widthNoScroll = outer.getBoundingClientRect().width;
    // force scrollbars
    outer.style.overflow = 'scroll';
    // add innerdiv
    const inner = createHtmlElement('div', { 'style': 'width:100%' });
    outer.appendChild(inner);
    const widthWithScroll = inner.getBoundingClientRect().width;
    // remove divs
    outer.parentNode.removeChild(outer);
    return widthNoScroll - widthWithScroll;
}
/**
 * addTouchPointer method \
 *
 * @returns {ITouches[]} addTouchPointer method .\
 * @param { ITouches[] } touchList - provide the touchList  value.
 * @param { PointerEvent } e - provide the e  value.
 * @param { TouchList } touches - provide the touches  value.
 * @private
 */
export function addTouchPointer(touchList, e, touches) {
    touchList = [];
    for (let i = 0, length = touches.length; i < length; i++) {
        touchList.push({
            pageX: touches[parseInt(i.toString(), 10)].clientX, pageY: touches[parseInt(i.toString(), 10)].clientY,
            pointerId: null
        });
    }
    return touchList;
}
/**
 * removes the element from dom \
 *
 * @returns {void} removes the element from dom .\
 * @param { ITouches[] } elementId - provide the elementId  value.
 * @param { PointerEvent } contentId - provide the contentId  value.
 * @private
 */
export function removeElement(elementId, contentId) {
    const div = getDiagramElement(elementId, contentId);
    if (div) {
        div.parentNode.removeChild(div);
    }
}
/**
 * getContent method   \
 *
 * @returns {void} getContent method .\
 * @param { DiagramHtmlElement | DiagramNativeElement } element - provide the elementId  value.
 * @param { boolean } isHtml - provide the boolean  value.
 * @param { Node | Annotation | PathAnnotation | NodeFixedUserHandle | ConnectorFixedUserHandle } nodeObject - provide the nodeObject  value.
 * @private
 */
export function getContent(element, isHtml, nodeObject) {
    let div;
    /* eslint-disable */
    if (isHtml) {
        const attr = { 'style': 'height: 100%; width: 100%' };
        div = createHtmlElement('div', attr);
    }
    else {
        div = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    }
    const diagramElement = document.getElementById(element.diagramId);
    const instance = 'ej2_instances';
    const diagram = diagramElement[instance][0];
    let node;
    if (!diagram.initNodeTemplate || !(diagram instanceof Diagram)) {
        node = getElement(element);
    }
    let content = '';
    let sentNode = {};
    let isSvg = false;
    let propertyName;
    if ((!node && diagram.initNodeTemplate)) {
        sentNode = sentNode = (diagram.tempTable && diagram.tempTable[element.nodeId]) || sentNode;
        propertyName = "nodeTemplate";
    }
    else if (node instanceof Node) {
        sentNode = node;
        ////Removed svg content re-assign from outerHtml to improve performance.
        //let blazor: string = 'Blazor';
        //Removed isBlazor code
        propertyName = "nodeTemplate";
    }
    else {
        sentNode = node;
        //new
        //Removed isBlazor code
        propertyName = "annotationTemplate";
    }
    let item;
    if ((typeof element.content === 'string' || typeof element.content === 'function') && (!element.isTemplate)) {
        const template = document.getElementById(element.content);
        if (template) {
            div.appendChild(template);
        }
        else {
            /* eslint-disable */
            let compiledString;
            compiledString = compile(element.content);
            for (item of compiledString(sentNode, diagram, propertyName, content)) {
                div.appendChild(item);
            }
            //new
            // for (item of compiledString(sentNode, null, null, content, undefined, undefined, isSvg)) {
            //     div.appendChild(item);
            // }
        }
    }
    else if (element.isTemplate) {
        let compiledString;
        if (diagram.isReact) {
            compiledString = element.getNodeTemplate()(
            /* eslint-enable */
            // eslint-disable-next-line quotes
            cloneObject(nodeObject), diagram, propertyName + "_" + ((propertyName === "nodeTemplate") ? nodeObject.id : element.nodeId + nodeObject.id), undefined, undefined, false, div);
        }
        else if (diagram.isVue || diagram.isVue3) {
            // EJ2-57563 - Added the below code to provide slot template support for Vue and Vue 3
            const templateFn = element.getNodeTemplate();
            if (templateFn) {
                // If other than slot template, this if block gets execute and template get returned.
                compiledString = element.getNodeTemplate()(
                /* eslint-enable */
                // eslint-disable-next-line quotes
                cloneObject(nodeObject), diagram, propertyName + "_" + ((propertyName === "nodeTemplate") ? nodeObject.id : element.nodeId + nodeObject.id), undefined, undefined, false, div);
            }
            else {
                // If we provide slot template means then it enters in this block and returns a template
                if (propertyName === 'nodeTemplate') {
                    compiledString = compile(diagram.nodeTemplate);
                }
                else {
                    compiledString = compile(diagram.annotationTemplate);
                }
                compiledString = compiledString(
                /* eslint-enable */
                // eslint-disable-next-line quotes
                cloneObject(nodeObject), diagram, propertyName + "_" + ((propertyName === "nodeTemplate") ? nodeObject.id : element.nodeId + nodeObject.id), undefined, undefined, false, div);
            }
        }
        else {
            compiledString = element.getNodeTemplate()(
            /* eslint-enable */
            // eslint-disable-next-line quotes
            cloneObject(nodeObject), diagram, propertyName + "_" + ((propertyName === "nodeTemplate") ? nodeObject.id : element.nodeId + nodeObject.id), undefined, undefined, false);
        }
        if (compiledString) {
            for (let i = 0; i < compiledString.length; i++) {
                div.appendChild(compiledString[parseInt(i.toString(), 10)]);
            }
        }
    }
    else {
        if (element.content && element.content.outerHTML) {
            div.appendChild(element.content);
        }
    }
    return element.isTemplate ?
        div : (isHtml ? div.cloneNode(true) : div.cloneNode(true));
}
/* eslint-enable */
/**
 * setAttributeSvg method   \
 *
 * @returns {void} setAttributeSvg method .\
 * @param { SVGElement } svg - provide the svg  value.
 * @param { Object } attributes - provide the boolean  value.
 * @private
 */
export function setAttributeSvg(svg, attributes) {
    const keys = Object.keys(attributes);
    for (let i = 0; i < keys.length; i++) {
        // Added below condition to check whether svg is undefined or not
        if (svg && keys[parseInt(i.toString(), 10)] !== 'style') {
            svg.setAttribute(keys[parseInt(i.toString(), 10)], attributes[keys[parseInt(i.toString(), 10)]]);
        }
        else {
            applyStyleAgainstCsp(svg, attributes[keys[parseInt(i.toString(), 10)]]);
        }
    }
}
/**
 * applyStyleAgainstCsp method   \
 *
 * @returns {void} applyStyleAgainstCsp method .\
 * @param { SVGElement } svg - provide the svg  value.
 * @param { string } attributes - provide the boolean  value.
 * @private
 */
export function applyStyleAgainstCsp(svg, attributes) {
    const keys = attributes.split(';');
    for (let i = 0; i < keys.length; i++) {
        const attribute = keys[parseInt(i.toString(), 10)].split(':');
        if (attribute.length === 2) {
            svg.style[attribute[0].trim()] = attribute[1].trim();
        }
    }
}
/**
 * setAttributeHtml method   \
 *
 * @returns {void} setAttributeHtml method .\
 * @param { HTMLElement } element - provide the svg  value.
 * @param { Object } attributes - provide the boolean  value.
 * @private
 */
export function setAttributeHtml(element, attributes) {
    const keys = Object.keys(attributes);
    for (let i = 0; i < keys.length; i++) {
        if (keys[parseInt(i.toString(), 10)] !== 'style') {
            element.setAttribute(keys[parseInt(i.toString(), 10)], attributes[keys[parseInt(i.toString(), 10)]]);
        }
        else {
            applyStyleAgainstCsp(element, attributes[keys[parseInt(i.toString(), 10)]]);
        }
    }
}
/**
 * createMeasureElements method   \
 *
 * @returns {void} createMeasureElements method .\
 * @private
 */
export function createMeasureElements() {
    const measureWindowElement = 'measureElement';
    if (!window[`${measureWindowElement}`]) {
        const divElement = createHtmlElement('div', {
            id: 'measureElement',
            style: 'visibility:hidden ; height: 0px ; width: 0px; overflow: hidden;'
        });
        const text = createHtmlElement('span', { 'style': 'display:inline-block ; line-height: normal' });
        divElement.appendChild(text);
        //let imageElement: HTMLImageElement;
        const imageElement = createHtmlElement('img', { 'alt': 'measureElementImage', 'src': 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7' });
        divElement.appendChild(imageElement);
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
        divElement.appendChild(svg);
        const element = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        element.setAttribute('d', '');
        svg.appendChild(element);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const data = document.createTextNode('');
        const tSpan = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        tSpan.setAttributeNS('http://www.w3.org/XML/1998/namespace', 'xml:space', 'preserve');
        svg.appendChild(tSpan);
        window[`${measureWindowElement}`] = divElement;
        window[`${measureWindowElement}`].usageCount = 1;
        document.body.appendChild(divElement);
        const measureElementCount = 'measureElementCount';
        if (!window[`${measureElementCount}`]) {
            window[`${measureElementCount}`] = 1;
        }
        else {
            window[`${measureElementCount}`]++;
        }
    }
    else {
        window[`${measureWindowElement}`].usageCount += 1;
    }
}
/**
 * setChildPosition method   \
 *
 * @returns {number} setChildPosition method .\
 * @param {SubTextElement} temp - provide the temp  value.
 * @param {SubTextElement[]} childNodes - provide the childNodes  value.
 * @param {number} i - provide the i  value.
 * @param {TextAttributes} options - provide the options  value.
 * @private
 */
export function setChildPosition(temp, childNodes, i, options) {
    if (childNodes.length >= 1 && temp.x === 0 &&
        (options.textOverflow === 'Clip' || options.textOverflow === 'Ellipsis') &&
        (options.textWrapping === 'Wrap' || options.textWrapping === 'WrapWithOverflow')) {
        temp.x = childNodes[i - 1] ? childNodes[i - 1].x : -(temp.width / 2);
        return temp.x;
    }
    return temp.x;
}
/**
 * getTemplateContent method\
 *
 * @returns {DiagramHtmlElement} getTemplateContent method .\
 * @param {DiagramHtmlElement} annotationcontent - provide the annotationcontent  value.
 * @param {Annotation} annotation - provide the annotation  value.
 * @param {number} annotationTemplate - provide the annotationTemplate  value.
 * @param {Object} diagram - provide the diagram value.
 * @private
 */
export function getTemplateContent(
// eslint-disable-next-line @typescript-eslint/ban-types
annotationcontent, annotation, annotationTemplate, diagram) {
    if ((annotationTemplate && !annotation.template) ||
        (annotation.template && typeof annotation.template === 'function' && diagram.isReact)) {
        annotationcontent.isTemplate = true;
        annotationcontent.template = annotationcontent.content = getContent(annotationcontent, true, annotation);
    }
    else {
        annotationcontent.content = annotation.template;
    }
    return annotationcontent;
}
/* eslint-disable */
/** @private */
export function createUserHandleTemplates(userHandleTemplate, template, selectedItems, diagramID) {
    let userHandleFn;
    let handle;
    let compiledString;
    let i;
    let div;
    let diagramElement = document.getElementById(diagramID);
    let instance = 'ej2_instances';
    let diagram = diagramElement[instance][0];
    if (userHandleTemplate && template) {
        userHandleFn = templateCompiler(userHandleTemplate);
        for (handle of selectedItems.userHandles) {
            if (userHandleFn) {
                compiledString = userHandleFn(cloneObject(handle), diagram, 'userHandleTemplate' + '_' + handle.name, undefined, undefined, false);
                for (i = 0; i < compiledString.length; i++) {
                    let attr = {
                        'style': 'height: 100%; width: 100%; pointer-events: all',
                        'id': handle.name + '_template_hiddenUserHandle'
                    };
                    div = createHtmlElement('div', attr);
                    div.appendChild(compiledString[i]);
                }
                template[0].appendChild(div);
            }
        }
    } //Removed isBlazor code
}
/* eslint-enable */
